/**
 * Thin progress bar at the bottom of the presenter view.
 *
 * Shows current slide position relative to total, plus visual "cut"
 * markers for the major narrative transitions:
 *   - Wechsel in den Interaktiven Workshop (slide 8)
 *   - Wechsel in das SVO OS (slide 21 — Live-Demo)
 *   - Reveal (slide 23 — Cliffhanger vor Reveal)
 */

import type { ProgressCut } from "@/engine/app/offsite-config";

interface Props {
  /** 0-based current slide index. */
  slideIndex: number;
  slideTotal: number;
  /** When true, presenter is in a workshop overlay (no slide visible). */
  workshopActive: boolean;
  /** 0-based slide index to jump to. When set, cuts become clickable. */
  onJump?: (index: number) => void;
  cuts: ProgressCut[];
}

export function PresenterProgressBar({
  slideIndex,
  slideTotal,
  workshopActive,
  onJump,
  cuts,
}: Props) {
  const currentSlide = slideIndex + 1; // 1-based
  const pct = (currentSlide / slideTotal) * 100;

  return (
    <div className="presenter-progress-bar">
      <div className="ppb-track" aria-hidden="true">
        <div className="ppb-fill" style={{ width: `${pct}%` }} />

        {cuts.map((c) => {
          // Place marker so fill lands exactly on it when currentSlide == atSlide.
          const cutPct = (c.atSlide / slideTotal) * 100;
          const passed = currentSlide >= c.atSlide;
          const className = `ppb-cut${passed ? " passed" : ""}${onJump ? " clickable" : ""}`;
          const inner = (
            <>
              <span className="ppb-cut-marker" />
              <span className="ppb-cut-label">
                <span className="ppb-cut-icon">{c.icon}</span>
                {c.label}
              </span>
            </>
          );
          if (onJump) {
            return (
              <button
                key={c.atSlide}
                type="button"
                className={className}
                style={{ left: `${cutPct}%` }}
                onClick={() => onJump(c.atSlide - 1)}
                title={`Springe zu Folie ${c.atSlide}`}
              >
                {inner}
              </button>
            );
          }
          return (
            <div
              key={c.atSlide}
              className={className}
              style={{ left: `${cutPct}%` }}
            >
              {inner}
            </div>
          );
        })}
      </div>

      <div className="ppb-meta">
        <span>
          {workshopActive ? (
            <strong>Workshop läuft</strong>
          ) : (
            <>
              Folie <strong>{currentSlide}</strong> / {slideTotal}
            </>
          )}
        </span>
      </div>
    </div>
  );
}
